import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Chip,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SavingsIcon from '@mui/icons-material/Savings';
import PercentIcon from '@mui/icons-material/Percent';
import { useNavigate, useParams } from 'react-router-dom';
import { prestadorFetch } from '../contexts/PrestadorAuthContext';
import PrestadorNotaMesDialog from '../components/prestador/PrestadorNotaMesDialog';
import { STATUS_NOTA_PORTAL } from '../constants/prestadorDocumentos';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const formatMoeda = (v) =>
  v != null && !Number.isNaN(Number(v))
    ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

function corStatus(status) {
  if (status === 'devolvida_prestador') return 'warning';
  if (status === 'pendente_contratos') return 'info';
  if (status === 'pago') return 'success';
  if (status === 'pendente' || !status) return 'default';
  return 'primary';
}

export default function PrestadorContratoPage() {
  const { numeroContrato } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [resumo, setResumo] = useState(null);
  const [meses, setMeses] = useState([]);
  const [mesDialog, setMesDialog] = useState({ open: false, mes: null });

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const enc = encodeURIComponent(numeroContrato);
      const [rRes, rMes] = await Promise.all([
        prestadorFetch(`/api/prestador/contratos/${enc}/resumo`),
        prestadorFetch(`/api/prestador/contratos/${enc}/meses`)
      ]);
      if (rRes.ok) setResumo(await rRes.json());
      if (rMes.ok) setMeses(await rMes.json());
    } finally {
      setLoading(false);
    }
  }, [numeroContrato]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  const cards = [
    { label: 'Notas emitidas', valor: resumo?.quantidadeNotas ?? 0, icon: <ReceiptLongIcon color="primary" /> },
    { label: 'Total bruto', valor: formatMoeda(resumo?.totalBruto), icon: <AccountBalanceIcon color="primary" /> },
    { label: 'Total líquido', valor: formatMoeda(resumo?.totalLiquido), icon: <SavingsIcon color="primary" /> },
    { label: 'Total impostos', valor: formatMoeda(resumo?.totalImpostos), icon: <PercentIcon color="primary" /> }
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/prestador/dashboard')}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Voltar ao painel
      </Button>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={700} color="primary">
          {resumo?.contrato?.empresa || numeroContrato}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Contrato {resumo?.contrato?.numeroContrato} · Vigência {resumo?.contrato?.vigenciaInicio || '—'} a{' '}
          {resumo?.contrato?.vigenciaFim || '—'}
        </Typography>
      </Paper>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
        Indicadores (BI)
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {cards.map((c) => (
          <Grid item xs={12} sm={6} md={3} key={c.label}>
            <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
              {c.icon}
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
                {c.valor}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {c.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {meses.some((m) => m.valorBruto != null) && (
        <>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Evolução por competência
          </Typography>
          <Paper sx={{ p: 2, mb: 4, borderRadius: 2, height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={meses
                  .filter((m) => m.valorBruto != null)
                  .map((m) => {
                    const [mesNome, ano] = String(m.competencia || '').split('/');
                    return {
                      mes: `${(mesNome || '').slice(0, 3)}/${ano || ''}`,
                      bruto: m.valorBruto || 0,
                      liquido: m.valorLiquido || 0,
                      impostos: m.valorImpostos || 0
                    };
                  })}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatMoeda(v)} />
                <Legend />
                <Bar dataKey="bruto" name="Bruto" fill="#1976d2" />
                <Bar dataKey="liquido" name="Líquido" fill="#2e7d32" />
                <Bar dataKey="impostos" name="Impostos" fill="#ed6c02" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
        Meses / competências
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Clique no mês para enviar a nota fiscal e documentos, ou para consultar o que já foi encaminhado.
        Após o envio, o setor de contratos analisa e pode devolver para correção.
      </Typography>

      <Grid container spacing={1.5}>
        {meses.map((m) => {
          const clicavel = m.podeEnviar || m.podeVisualizar;
          return (
          <Grid item xs={6} sm={4} md={3} key={m.competencia}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                cursor: clicavel ? 'pointer' : 'default',
                border: '1px solid',
                borderColor: m.notaId ? 'primary.light' : 'divider',
                opacity: clicavel ? 1 : 0.85,
                '&:hover': clicavel ? { bgcolor: 'action.hover' } : {}
              }}
              onClick={() => clicavel && setMesDialog({ open: true, mes: m })}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                {m.competencia}
              </Typography>
              <Chip
                size="small"
                label={STATUS_NOTA_PORTAL[m.status] || m.status}
                color={corStatus(m.status)}
                sx={{ mt: 1 }}
              />
              {m.valorBruto != null && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Bruto: {formatMoeda(m.valorBruto)}
                </Typography>
              )}
            </Paper>
          </Grid>
          );
        })}
      </Grid>

      <PrestadorNotaMesDialog
        open={mesDialog.open}
        mes={mesDialog.mes}
        numeroContrato={numeroContrato}
        onClose={() => setMesDialog({ open: false, mes: null })}
        onSalvo={carregar}
      />
    </Box>
  );
}
